import { useField } from "formik";
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef
} from "react";
import { FormControl } from "@mui/material";
import { FieldError, useController, useFormContext } from "react-hook-form";
import { useYupContext } from "../../../context/RequiredFields.context";

export interface MxFieldControllerContext {
  inputRef: React.RefObject<any>;
  onChange: (e: { target: { value: string | any; name: string } }) => void;
  onBlur: (
    e?: { target: { value: string | any; name: string } } | undefined
  ) => void;
  value: any;
  disabled?: boolean;
  invalid: boolean;
  error?: FieldError;
  errorMessage?: string;
  testId: string;
  htmlFor: string;
  id: string;
  name: string;
  required?: boolean;
}

const MxFieldControllerContextInstance =
  createContext<MxFieldControllerContext | null>(null);

interface IMxFieldControllerContextProps {
  name: string;
  children: React.ReactNode;
  // Prefer boolean; legacy numeric kept for compatibility
  withReactHookForm?: boolean;
  withreacthookform?: 1 | 0;
}

function MxFieldControllerContext(props: IMxFieldControllerContextProps) {
  const name = String(props.name); // Ensure name is a string
  const useRHF = props.withReactHookForm ?? props.withreacthookform === 1;
  return useRHF ? (
    <WithReactHookForm {...props} name={name} />
  ) : (
    <WithFormik {...props} name={name} />
  );
}

// Component for handling fields with Formik
const WithFormik = memo((props: IMxFieldControllerContextProps) => {
  const name = String(props.name); // Ensure name is a string
  // const { requiredFieldsRef } = useRequiredField();
  const { requiredFields } = useYupContext();

  const ref = useRef<any>();

  // Use the Formik's `useField` hook to get control over the input
  const [field, meta, helpers] = useField({
    name: name,
    "aria-label": `${name}-label`
  });

  const handleBlur = useCallback(
    (e: any) => {
      field.onBlur(e);
    },
    [field.onBlur]
  );

  const error = Boolean(meta.touched && meta.error);
  const isRequired = !!requiredFields[props.name];
  const errorMessage = error ? meta.error || "" : undefined;

  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo<MxFieldControllerContext>(
    () => ({
      inputRef: ref,
      onChange: field.onChange,
      onBlur: handleBlur,
      value: field.value,
      disabled: false,
      invalid: error,
      required: isRequired,
      error: error
        ? { type: "required", message: errorMessage || "", ref: ref.current }
        : undefined,
      errorMessage,
      testId: `test-${name}`,
      htmlFor: `htmlFor-${name}`,
      id: `id-${name}`,
      name: name
    }),
    [field.onChange, handleBlur, field.value, error, isRequired, errorMessage, name]
  );
  return (
    <MxFieldControllerContextInstance.Provider value={value}>
      <FormControl
        fullWidth
        data-field-name={name}
        required={isRequired}
        error={value.invalid}
        data-testid={value.testId}
        id={value.id}
        className={`mx-field-${value.id}-container`}
      >
        {props.children}
      </FormControl>
    </MxFieldControllerContextInstance.Provider>
  );
});

// Component for handling fields with React Hook Form
const WithReactHookForm = memo((props: IMxFieldControllerContextProps) => {
  const name = String(props.name);
  const { control } = useFormContext();

  const {
    field: { ref, onBlur, onChange, value, ...field },
    fieldState: { isTouched, invalid, error, isDirty }
  } = useController({
    name: name,
    control
  });

  // Memoize the context value to avoid unnecessary re-renders
  const errorMessage = error?.message;
  const state = useMemo<MxFieldControllerContext>(
    () => ({
      inputRef: ref as any,
      onChange,
      onBlur,
      value,
      disabled: field.disabled,
      invalid,
      error,
      errorMessage,
      testId: `test-${name}`,
      htmlFor: `htmlFor-${name}`,
      id: `id-${name}`,
      name: name
    }),
    [
      onChange,
      onBlur,
      value,
      invalid,
      errorMessage,
      field.disabled,
      name,
      JSON.stringify(error),
      isTouched,
      isDirty
    ]
  );

  return (
    <MxFieldControllerContextInstance.Provider value={state}>
      <FormControl
        fullWidth
        data-field-name={name}
        error={state.invalid}
        data-testid={state.testId}
        id={state.id}
        className={`mx-field-${state.id}-container`}
      >
        {props.children}
      </FormControl>
    </MxFieldControllerContextInstance.Provider>
  );
});

// Custom hook to consume the context
function useMxFieldControllerContext() {
  const context = useContext(MxFieldControllerContextInstance);
  if (!context) {
    throw new Error(
      "useMxFieldControllerContext must be used within a MxFieldControllerContextInstance.Provider"
    );
  }
  return context;
}

export { useMxFieldControllerContext };
export default MxFieldControllerContext;
